// ---------------------------------------------------------------------------
// PROJECT CLOSING — single source of truth for homeowner closing outcomes.
// ---------------------------------------------------------------------------
// The homeowner records HOW a project ended. The database trigger
// `mkt_listing_closing_guard` is the authority:
//   • validates the hired contractor actually unlocked (purchased) the lead
//   • requires a contractor selection for hired/work-completed outcomes when
//     at least one contractor purchased the lead (unless "not from GigGone")
//   • maps outcome -> mkt_listings.status (server side, never trusted from UI)
//        hired_contractor -> 'hired'      (off the public feed, job in progress)
//        work_completed   -> 'completed'  (stamps completed_at -> the daily
//                                          testimonial-auto-request job sends
//                                          the existing review request)
//        did_not_proceed / cancelled / other -> 'cancelled'
// This module only declares labels + the client calls. Never retype the
// outcome keys elsewhere — import them from here.
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabase';

export type ClosingOutcome = 'hired_contractor' | 'work_completed' | 'did_not_proceed' | 'cancelled' | 'other';

export interface OutcomeDef {
  key: ClosingOutcome;
  en: string;
  es: string;
  descEn: string;
  descEs: string;
  /** true = a contractor was hired, so a contractor selection is required when purchasers exist */
  needsContractor: boolean;
  tone: string;
}

export const CLOSING_OUTCOMES: OutcomeDef[] = [
  {
    key: 'hired_contractor',
    en: 'Hired a contractor',
    es: 'Contraté a un contratista',
    descEn: 'You picked a contractor and the work is scheduled or in progress.',
    descEs: 'Elegiste un contratista y el trabajo está programado o en curso.',
    needsContractor: true,
    tone: 'bg-indigo-50 text-indigo-800 ring-indigo-200',
  },
  {
    key: 'work_completed',
    en: 'Work completed',
    es: 'Trabajo terminado',
    descEn: 'The job is finished. We will ask you for a short review.',
    descEs: 'El trabajo está terminado. Te pediremos una breve reseña.',
    needsContractor: true,
    tone: 'bg-sky-50 text-sky-800 ring-sky-200',
  },
  {
    key: 'did_not_proceed',
    en: 'Did not proceed',
    es: 'No seguí adelante',
    descEn: 'You decided not to do the project for now.',
    descEs: 'Decidiste no hacer el proyecto por ahora.',
    needsContractor: false,
    tone: 'bg-slate-100 text-slate-700 ring-slate-200',
  },
  {
    key: 'cancelled',
    en: 'Cancelled',
    es: 'Cancelado',
    descEn: 'The project is cancelled and should not be shown anymore.',
    descEs: 'El proyecto está cancelado y ya no debe mostrarse.',
    needsContractor: false,
    tone: 'bg-red-50 text-red-700 ring-red-200',
  },
  {
    key: 'other',
    en: 'Other',
    es: 'Otro',
    descEn: 'Something else happened — tell us briefly.',
    descEs: 'Pasó otra cosa — cuéntanos brevemente.',
    needsContractor: false,
    tone: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
];

export const outcomeDef = (key?: string | null) => CLOSING_OUTCOMES.find((o) => o.key === key);

/** Statuses from which a homeowner may open the closing flow. */
export const CLOSABLE_STATUSES = ['published', 'active', 'sold_out', 'pending_review', 'hired'];

/** Sentinel value for "I hired someone who is not on GigGone". */
export const OFF_PLATFORM = '__off_platform__';

export interface HiringCandidate {
  buyer_id: string;
  name: string;
  trades: string[];
  unlocked_at: string;
}

/**
 * Contractors who purchased (unlocked) this lead. RLS: the owner can read the
 * purchases of their own listing, and p_profiles_read returns only profiles
 * linked through an actual purchase — so this is exactly the eligible set.
 */
export const loadHiringCandidates = async (listingId: string): Promise<HiringCandidate[]> => {
  const { data: purchases } = await supabase
    .from('mkt_lead_purchases')
    .select('buyer_id,created_at')
    .eq('listing_id', listingId)
    .order('created_at', { ascending: true });
  const ids = [...new Set((purchases || []).map((p: any) => p.buyer_id).filter(Boolean))] as string[];
  if (!ids.length) return [];
  const { data: profiles } = await supabase
    .from('mkt_profiles')
    .select('id,company_name,contact_name,first_name,last_name,trades')
    .in('id', ids);
  const map = new Map((profiles || []).map((p: any) => [p.id, p]));
  return ids.map((id, i) => {
    const p: any = map.get(id) || {};
    const name =
      (p.company_name || '').trim() ||
      (p.contact_name || '').trim() ||
      `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
      `Contractor ${i + 1}`;
    const first = (purchases || []).find((x: any) => x.buyer_id === id) as any;
    return { buyer_id: id, name, trades: p.trades || [], unlocked_at: first?.created_at || '' };
  });
};

export interface ClosePayload {
  outcome: ClosingOutcome;
  /** buyer_id of the hired contractor, OFF_PLATFORM, or '' */
  hiredSelection?: string;
  finalJobValue?: number | null;
  note?: string;
}

/** Map raw DB errors from the closing guard to friendly bilingual copy. */
export const closingErrorMessage = (msg: string, es: boolean): string => {
  if (/hired_contractor_required/.test(msg))
    return es ? 'Selecciona el contratista que contrataste.' : 'Please select the contractor you hired.';
  if (/hired_contractor_not_purchaser/.test(msg))
    return es ? 'Ese contratista no desbloqueó este proyecto.' : 'That contractor did not unlock this project.';
  if (/listing_under_moderation/.test(msg))
    return es ? 'Este proyecto está en revisión y no se puede cerrar ahora.' : 'This project is under review and cannot be closed right now.';
  return es ? 'No se pudo cerrar el proyecto. Inténtalo de nuevo.' : 'Could not close the project. Please try again.';
};

/**
 * Record the outcome. Only the closing columns are written — status is set by
 * the database trigger from the outcome. Returns the updated status or an error.
 */
export const closeProject = async (
  listingId: string,
  p: ClosePayload
): Promise<{ status?: string; error?: string }> => {
  const def = outcomeDef(p.outcome);
  if (!def) return { error: 'invalid_outcome' };
  const offPlatform = def.needsContractor && p.hiredSelection === OFF_PLATFORM;
  const buyer = def.needsContractor && p.hiredSelection && p.hiredSelection !== OFF_PLATFORM ? p.hiredSelection : null;
  const value =
    def.needsContractor && p.finalJobValue != null && Number.isFinite(p.finalJobValue) && p.finalJobValue >= 0
      ? Math.round(p.finalJobValue)
      : null;
  const { data, error } = await supabase
    .from('mkt_listings')
    .update({
      closing_outcome: p.outcome,
      hired_buyer_id: buyer,
      hired_off_platform: offPlatform,
      final_job_value: value,
      closing_note: (p.note || '').trim().slice(0, 500) || null,
    })
    .eq('id', listingId)
    .select('status')
    .maybeSingle();
  if (error) return { error: error.message || 'update_failed' };
  return { status: (data as any)?.status };
};
